/** Clients, leurs sites, contacts et machines. */
import { lazy, type ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import type { AppRoute } from '../../app/route-types.js';

const CustomersPage = lazy(() => import('./CustomersPage.js').then((m) => ({ default: m.CustomersPage })));
const ContactImportPage = lazy(() => import('./ContactImportPage.js').then((m) => ({ default: m.ContactImportPage })));
const CustomerTagsPage = lazy(() => import('./CustomerTags.js').then((m) => ({ default: m.CustomerTagsPage })));
const CustomerNewPage = lazy(() => import('./CustomerNewPage.js').then((m) => ({ default: m.CustomerNewPage })));
const CustomerInviteNewPage = lazy(() => import('./CustomerNewPage.js').then((m) => ({ default: m.CustomerInviteNewPage })));
const CustomerEditPage = lazy(() => import('./CustomerEditPage.js').then((m) => ({ default: m.CustomerEditPage })));
const SiteNewPage = lazy(() => import('./CustomerChildPages.js').then((m) => ({ default: m.SiteNewPage })));
const SiteEditPage = lazy(() => import('./CustomerChildPages.js').then((m) => ({ default: m.SiteEditPage })));
const ContactNewPage = lazy(() => import('./CustomerChildPages.js').then((m) => ({ default: m.ContactNewPage })));
const EquipmentNewPage = lazy(() => import('./CustomerChildPages.js').then((m) => ({ default: m.EquipmentNewPage })));
const CustomerMergePage = lazy(() => import('./CustomerMergePage.js').then((m) => ({ default: m.CustomerMergePage })));
const PhonebookPage = lazy(() => import('./PhonebookPage.js').then((m) => ({ default: m.PhonebookPage })));
const CustomerDetail = lazy(() => import('./CustomerDetail.js').then((m) => ({ default: m.CustomerDetail })));

/**
 * Ancienne page « Coordonnées » (signets, liens des écrans d'avant le
 * 17/10/2026) : la modification de la fiche réunit désormais tout.
 */
function ContactPageRedirect(): ReactNode {
  const { id = '' } = useParams();
  const { state } = useLocation();
  return <Navigate to={`/clients/${id}/modifier`} replace state={state} />;
}

const view = { anyOf: ['customer.view'] } as const;
const manage = { anyOf: ['customer.manage'] } as const;

export const customersRoutes: AppRoute[] = [
  { path: '/clients', element: <CustomersPage />, gate: view },
  { path: '/clients/etiquettes', element: <CustomerTagsPage />, gate: view },
  // Aperçu et import exigent `customer.manage` côté serveur : une page qui ne pourrait que répondre 403 ne s'ouvre pas.
  { path: '/clients/importer', element: <ContactImportPage />, gate: manage },
  { path: '/clients/repertoire', element: <PhonebookPage />, gate: { anyOf: ['customer.view'] } },
  { path: '/clients/:id/fusionner', element: <CustomerMergePage />, gate: { allOf: ['customer.manage', 'customer.view'] } },
  { path: '/clients/nouveau', element: <CustomerNewPage />, gate: manage },
  { path: '/clients/nouveau/lien-client', element: <CustomerInviteNewPage />, gate: manage },
  { path: '/clients/:id/modifier', element: <CustomerEditPage />, gate: manage },
  { path: '/clients/:id/coordonnees', element: <ContactPageRedirect /> },
  { path: '/clients/:id/sites/nouveau', element: <SiteNewPage />, gate: manage },
  { path: '/clients/:id/sites/:siteId/modifier', element: <SiteEditPage />, gate: manage },
  { path: '/clients/:id/contacts/nouveau', element: <ContactNewPage />, gate: manage },
  { path: '/clients/:id/machines/nouvelle', element: <EquipmentNewPage />, gate: { anyOf: ['equipment.manage'] } },
  { path: '/clients/:id', element: <CustomerDetail />, gate: view },
];
